import {
  WhmSkeleton,
  WhmStack,
  WhmTab,
  WhmTabs,
  WhmTypography,
} from "@securitize/reactjs-whm";
import {
  useGetAssets,
  useGetAssetsSummary,
  useGetFundBalances,
  useGetInProgressInvestments,
  useGetPortfolioPerformance,
  useGetPreviouslyOwnedAssets,
} from "../../../../data/usePortfolio";
import { useEffect, useState } from "react";
import MyAssets from "./components/MyAssets";
import { ReactComponent as MyAssetsIcon } from "@securitize/assets-sec-ui/dist/icons/svg/list.svg";
import { ReactComponent as InProgressIcon } from "@securitize/assets-sec-ui/dist/icons/svg/pending.svg";
import { ReactComponent as PreviouslyOwnedIcon } from "@securitize/assets-sec-ui/dist/icons/svg/timeless.svg";
import { ReactComponent as SfsAssetsIcon } from "@securitize/assets-sec-ui/dist/icons/svg/bank.svg";
import { Translate } from "../../../Translation/Translate";
import { translationKeys } from "../../../../contexts/I18nContext";
import { ErrorPage } from "../../../shared/ErrorPage";
import InProgress from "./components/InProgress";
import PreviouslyOwned from "./components/PreviouslyOwned/PreviouslyOwned";
import SfsAssets from "./components/SfsAssets";
import TabIcon from "../../../shared/TabIcon";
import useUserInfo from "../../../../hooks/useUserInfo";
import { KycStatus } from "../../../../types/KycStatus";
import config from "../../../../data/config";

export default function Assets() {
  const { verificationStatus } = useUserInfo();
  const assetsSummaryQuery = useGetAssetsSummary();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [currentTab, setCurrentTab] = useState("my-assets");

  // Preload
  useGetAssets();
  useGetPortfolioPerformance();
  useGetInProgressInvestments();
  useGetPreviouslyOwnedAssets();
  useGetFundBalances();

  useEffect(() => {
    if (isFirstLoad && assetsSummaryQuery.isSuccess) {
      if (
        assetsSummaryQuery.data.owned === 0 &&
        assetsSummaryQuery.data.inProgress > 0
      ) {
        setCurrentTab("in-progress");
      }
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, assetsSummaryQuery]);

  const isVerified = verificationStatus === KycStatus.VERIFIED;

  if (assetsSummaryQuery.isError) {
    return (
      <ErrorPage
        errorCode={assetsSummaryQuery.error.correlationId}
        onRefresh={() => assetsSummaryQuery.refetch()}
      />
    );
  }

  if (assetsSummaryQuery.isPending) {
    return (
      <WhmStack gap="16px">
        <WhmStack direction="row" gap="4px">
          <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={40} />
          <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={40} />
          <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={40} />
        </WhmStack>
        <MyAssets /> {/* Preload my-assets while displaying skeleton */}
      </WhmStack>
    );
  }

  // SFS balances are only counted while the feature is on, so with it off the
  // tabs behave exactly as before
  const enableSfsAssetsTab =
    config.FEATURE_FLAGS.PORTFOLIO_SFS_ASSETS_TAB_ENABLED &&
    assetsSummaryQuery.data.sfsBalances > 0;

  const hasSidAssets =
    assetsSummaryQuery.data.inProgress > 0 ||
    assetsSummaryQuery.data.owned > 0 ||
    assetsSummaryQuery.data.previouslyOwned > 0;

  // Users with only SFS balances see My Assets (empty state) and the SFS tab, not In Progress
  const hasOnlySfsBalances = enableSfsAssetsTab && !hasSidAssets;

  const hideTabs = !hasSidAssets && !enableSfsAssetsTab;

  const enablePreviouslyOwnedTab = Boolean(
    assetsSummaryQuery.data.previouslyOwned,
  );

  if (!isVerified && hideTabs) {
    return null;
  }

  return (
    <WhmStack gap="16px" paddingBottom="64px">
      {!hideTabs && (
        <WhmTabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue as string)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <WhmTab
            value="my-assets"
            sx={{ textTransform: "none" }}
            label={
              <WhmStack direction="row" gap="6px">
                <MyAssetsIcon fill="currentColor" />
                <WhmTypography>
                  <Translate
                    translationKey={translationKeys.PORTFOLIO_ASSETS_MY_ASSETS}
                  />
                </WhmTypography>
              </WhmStack>
            }
          />
          {!hasOnlySfsBalances && (
            <WhmTab
              value="in-progress"
              sx={{ textTransform: "none" }}
              label={
                <WhmStack direction="row" gap="6px" alignItems="center">
                  <TabIcon
                    hasBadge={Boolean(assetsSummaryQuery.data.inProgress)}
                    icon={<InProgressIcon fill="currentColor" />}
                  />
                  <WhmTypography>
                    <Translate
                      translationKey={
                        translationKeys.PORTFOLIO_ASSETS_IN_PROGRESS
                      }
                    />
                  </WhmTypography>
                </WhmStack>
              }
            />
          )}
          {enablePreviouslyOwnedTab && (
            <WhmTab
              value="previously-owned"
              sx={{ textTransform: "none" }}
              label={
                <WhmStack direction="row" gap="6px">
                  <PreviouslyOwnedIcon fill="currentColor" />
                  <WhmTypography>
                    <Translate
                      translationKey={
                        translationKeys.PORTFOLIO_ASSETS_PREVIOUSLY_OWNED
                      }
                    />
                  </WhmTypography>
                </WhmStack>
              }
            />
          )}
          {enableSfsAssetsTab && (
            <WhmTab
              value="sfs-assets"
              sx={{ textTransform: "none" }}
              label={
                <WhmStack direction="row" gap="6px">
                  <SfsAssetsIcon fill="currentColor" />
                  <WhmTypography>
                    <Translate
                      translationKey={translationKeys.PORTFOLIO_ASSETS_SFS}
                    />
                  </WhmTypography>
                </WhmStack>
              }
            />
          )}
        </WhmTabs>
      )}
      {currentTab === "my-assets" && <MyAssets />}
      {currentTab === "in-progress" && <InProgress />}
      {currentTab === "previously-owned" && <PreviouslyOwned />}
      {currentTab === "sfs-assets" && <SfsAssets />}
    </WhmStack>
  );
}
